/**
 * Provides a Stripe Elements context for collecting a payment method.
 * Creates a SetupIntent on mount and initializes Stripe Elements with the
 * resulting client secret so children can render a PaymentElement.
 *
 * Ported from the deerlake billing flow (StripeElementsProvider).
 */
import { useState, useEffect, type ReactNode } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';

import { Loading, Text } from '@/ui';
import { createSetupIntent } from '@/utils/helpers/stripeServices';

const stripePublicKey = import.meta.env.VITE_STRIPE_PK;
const stripePromise = stripePublicKey ? loadStripe(stripePublicKey) : null;

type Props = {
  stripeCustomerId: string;
  children: ReactNode;
};

export const StripeElementsProvider = ({ stripeCustomerId, children }: Props) => {
  const { t } = useTranslation();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    createSetupIntent(stripeCustomerId)
      .then((result) => {
        if (!cancelled) setClientSecret(result.client_secret);
      })
      .catch(() => {
        if (!cancelled) setError(t('Companies.deferred_fees_card_init_error'));
      });
    return () => {
      cancelled = true;
    };
  }, [stripeCustomerId, t]);

  if (error) {
    return (
      <Text size='s' weight='medium' color='red'>
        {error}
      </Text>
    );
  }

  if (!clientSecret || !stripePromise) {
    return (
      <div className='relative flex h-32 items-center justify-center'>
        <Loading />
      </div>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            fontFamily: 'Poppins, sans-serif',
            borderRadius: '8px',
          },
        },
      }}
    >
      {children}
    </Elements>
  );
};
